import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { WhatsAppIneaCard } from '@/components/WhatsAppIneaCard';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, Target, Heart, Sparkles, Radio, Compass, Briefcase, RotateCcw } from 'lucide-react';
import { motion } from 'framer-motion';
import { apiRequest, isQuotaError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { SectorPicker } from '@/components/SectorPicker';
import { openGuidedTour } from '@/components/GuidedTour';
interface ExtractedProfile {
  first_name: string | null;
  age: number | null;
  passions: string[];
  current_results: string | null;
  goals: string[];
  positioning: 'voyage' | 'business' | 'niche' | null;
  platforms: string[];
  tone: 'inspirant' | 'expert' | 'authentique' | 'niche' | null;
}

const POSITIONING_LABEL: Record<string, string> = {
  voyage: 'Voyage & Lifestyle',
  business: 'Business',
  niche: 'Niche',
};

const TONE_LABEL: Record<string, string> = {
  inspirant: 'Inspirant',
  expert: 'Expert',
  authentique: 'Authentique',
  niche: 'Niche',
};

/** Profile page — shows the structured fiche extracted by Inea (Module 1 output). */
export default function ProfilePage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<{ profile: ExtractedProfile; status: string }>({
    queryKey: ['onboarding', 'profile'],
    queryFn: () => apiRequest('/onboarding/profile'),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-pink" size={30} />
      </div>
    );
  }

  const profile = data?.profile;
  if (!profile) {
    return (
      <div className="mx-auto max-w-md px-5 py-20 text-center">
        <p className="text-ink/60">{t("profile.missingProfile")}</p>
        <button onClick={() => navigate('/onboarding')} className="btn-primary mt-5">
          {t('profile.startOnboarding')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[800px] px-5 py-10 sm:px-8">
      <motion.header
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex items-center gap-3.5"
      >
        <div>
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t("profile.title")}
          </p>
          <h1 className="font-display text-[clamp(22px,3vw,30px)] font-800 tracking-[-0.025em]">
            {profile.first_name ?? 'Entrepreneur'}
            {profile.age ? <span className="text-ink/45"> · {profile.age} ans</span> : null}
          </h1>
        </div>
      </motion.header>

      <div className="grid gap-4 sm:grid-cols-2">
        {profile.positioning && (
          <InfoCard
            icon={<MapPin size={18} />}
            label="Positionnement"
            delay={0.05}
          >
            <span className="text-grad font-display text-xl font-800">
              {POSITIONING_LABEL[profile.positioning]}
            </span>
          </InfoCard>
        )}

        {profile.tone && (
          <InfoCard icon={<Radio size={18} />} label="Ton de communication" delay={0.1}>
            <span className="font-display text-xl font-700 text-ink">
              {TONE_LABEL[profile.tone]}
            </span>
          </InfoCard>
        )}

        {Array.isArray(profile.passions) && profile.passions.length > 0 && (
          <InfoCard icon={<Heart size={18} />} label="Passions" delay={0.15}>
            <TagList items={profile.passions} />
          </InfoCard>
        )}

        {Array.isArray(profile.platforms) && profile.platforms.length > 0 && (
          <InfoCard icon={<Sparkles size={18} />} label="Plateformes" delay={0.2}>
            <TagList items={profile.platforms.map(capitalize)} />
          </InfoCard>
        )}

        {Array.isArray(profile.goals) && profile.goals.length > 0 && (
          <InfoCard icon={<Target size={18} />} label="Objectifs" delay={0.25} wide>
            <ul className="space-y-1.5">
              {profile.goals.map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] text-ink/75">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gradient-to-br from-blue to-pink" />
                  {g}
                </li>
              ))}
            </ul>
          </InfoCard>
        )}

        {profile.current_results && (
          <InfoCard icon={<Sparkles size={18} />} label="Situation actuelle" delay={0.3} wide>
            <p className="text-[14px] leading-[1.55] text-ink/75">{profile.current_results}</p>
          </InfoCard>
        )}
      </div>

      {/* Sectors — universal multi-sector picker (Network-iA tool large) */}
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="card mt-8 p-6"
      >
        <header className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 text-white">
            <Briefcase size={18} />
          </span>
          <div>
            <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
              {t('profile.sectorsKicker')}
            </p>
            <h2 className="font-display text-xl font-800 tracking-[-0.015em]">
              {t('profile.sectorsTitle')}
            </h2>
          </div>
        </header>
        <SectorPicker compact />
      </motion.section>

      {/* 12/10/2026 — compléter ou corriger ses informations : l'entretien se
          rouvre (nouvelle session), le profil est fusionné à la fin, l'identité
          attend un « Régénérer » explicite. Ticket TKT-2026-0008. */}
      <RestartInterviewCard />

      {/* Lot 3.1 — Inéa sur WhatsApp (liaison par code) */}
      <WhatsAppIneaCard />

      <div className="mt-8 flex flex-wrap gap-3">
        {/* Même précaution que dans OnboardingPage (17/09/2026) : /profil est
            atteignable avec un onboarding_completed périmé à false, et sans ce
            rafraîchissement le clic renverrait l'utilisateur sur l'entretien. */}
        <button
          onClick={() => { void refresh().catch(() => undefined).finally(() => navigate('/diagnostic')); }}
          className="btn-primary"
        >
          <Compass size={16} /> {t('profile.ctaDiagnostic')}
        </button>
        <button onClick={() => navigate('/')} className="btn-ghost">
          {t('profile.ctaSpace')}
        </button>
        <button onClick={() => navigate('/onboarding')} className="btn-ghost">
          {t('profile.ctaInterview')}
        </button>
        <button onClick={openGuidedTour} className="btn-ghost">
          {t('tour.replay')}
        </button>
      </div>
    </div>
  );
}

/**
 * Recommencer l'entretien avec Inea — confirmation en ligne (pas de fenêtre
 * système), puis POST /onboarding/restart et départ vers /onboarding, où la
 * conversation vide déclenche d'elle-même la première question d'Inea.
 */
function RestartInterviewCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const restart = useMutation({
    mutationFn: () => apiRequest<{ id: string; status: string }>('/onboarding/restart', { method: 'POST' }),
    // La session propre vient du serveur ; la fiche lit profiles.profile_data,
    // qui ne change qu'à la fin du nouvel entretien : rien à invalider ici.
    onSuccess: () => navigate('/onboarding'),
  });

  return (
    <motion.section
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3, duration: 0.4 }}
      className="card mt-6 p-6"
    >
      <header className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue to-pink text-white">
          <RotateCcw size={18} />
        </span>
        <div>
          <p className="font-mono text-[11px] font-700 uppercase tracking-[0.18em] text-pink">
            {t('profile.restartKicker')}
          </p>
          <h2 className="font-display text-xl font-800 tracking-[-0.015em]">{t('profile.restartTitle')}</h2>
        </div>
      </header>
      <p className="text-[13.5px] leading-[1.6] text-ink/70">{t('profile.restartBody')}</p>
      {restart.isError && (
        <p className="mt-3 rounded-xl bg-pink/10 px-3.5 py-2.5 text-[13px] font-500 text-pink-deep">
          {isQuotaError(restart.error) ? t('quota.title') : t('profile.restartError')}
        </p>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        {!confirming ? (
          <button onClick={() => setConfirming(true)} className="btn-primary">
            <RotateCcw size={16} /> {t('profile.restartCta')}
          </button>
        ) : (
          <>
            <button
              onClick={() => restart.mutate()}
              disabled={restart.isPending}
              autoFocus
              className="btn-primary disabled:opacity-60"
            >
              {restart.isPending ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
              {t('profile.restartConfirm')}
            </button>
            <button onClick={() => setConfirming(false)} disabled={restart.isPending} className="btn-ghost">
              {t('common.cancel')}
            </button>
          </>
        )}
      </div>
    </motion.section>
  );
}

function InfoCard({
  icon,
  label,
  children,
  delay,
  wide,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  delay: number;
  wide?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`card p-5 ${wide ? 'sm:col-span-2' : ''}`}
    >
      <div className="mb-3 flex items-center gap-2">
        <span
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-white"
          style={{ background: 'linear-gradient(135deg, #3b82f6, #ec4899)' }}
        >
          {icon}
        </span>
        <span className="font-mono text-[10px] font-700 uppercase tracking-[0.14em] text-ink/50">
          {label}
        </span>
      </div>
      {children}
    </motion.div>
  );
}

function TagList({ items }: { items: string[] }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t, i) => (
        <span
          key={i}
          className="rounded-full border border-ink/[0.10] bg-ink/[0.03] px-3 py-1 text-[13px] font-500 text-ink/75"
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
